import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { FileText, ArrowRight, UploadCloud } from "lucide-react";
import { motion } from "framer-motion";

export default function ReportsHistory() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api.get("/reports").then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, []);

  return (
    <DashboardLayout
      title="Reports"
      subtitle="Your communication analysis reports."
      action={
        <Button asChild className="bg-cyan-400 text-black font-semibold hover:bg-cyan-300">
          <Link to="/upload" data-testid="reports-new-btn"><UploadCloud className="h-4 w-4 mr-2" />New Analysis</Link>
        </Button>
      }
    >
      {rows && rows.length === 0 ? (
        <div className="rounded-xl border border-slate-800 bg-[#0A1124] flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full border border-slate-700 bg-slate-900">
            <FileText className="h-5 w-5 text-slate-500" />
          </div>
          <p className="text-slate-400">No reports yet.</p>
          <Button asChild variant="outline" className="border-slate-700 text-white hover:bg-slate-800 hover:text-white">
            <Link to="/upload" data-testid="reports-empty-cta">Run an analysis</Link>
          </Button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {(rows || []).map((r, i) => (
            <motion.div
              key={r.report_id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <Link
                to={`/reports/${r.report_id}`}
                data-testid={`report-card-${r.report_id}`}
                className="group block rounded-xl border border-slate-800 bg-[#0A1124] p-6 transition-all hover:-translate-y-1 hover:border-cyan-400/40"
              >
                <div className="flex items-start justify-between">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-700 bg-slate-900">
                    <FileText className="h-4 w-4 text-cyan-400" />
                  </div>
                  <ArrowRight className="h-4 w-4 text-slate-600 transition-colors group-hover:text-cyan-400" />
                </div>
                <p className="mt-4 truncate font-medium text-white">{r.filename}</p>
                <p className="mt-1 text-xs text-slate-500 font-mono">{formatDate(r.created_at)}</p>
                <p className="mt-1 text-xs text-slate-600 font-mono">{r.report_id}</p>
              </Link>
            </motion.div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
